/**
 * TotalCoin — Gestión de cuentas, webhooks y movimientos.
 * Patrón: copia el estilo de Bancos.jsx (Tailwind + shadcn/ui + lucide).
 *
 * Tabs:
 *   - Cuentas: CRUD + test de conexión + URL del webhook (copy)
 *   - Cashin: eventos entrantes en vivo
 *   - Cashout: solicitudes de pago saliente
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Wallet, Plus, RefreshCw, Copy, Check, Trash2, Pencil, X,
  CheckCircle2, AlertTriangle, ShieldCheck, ArrowDownToLine, ArrowUpFromLine,
  Eye, EyeOff, ExternalLink, KeyRound, RotateCw, Loader2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: 'cuentas',  label: 'Cuentas',  icon: Wallet },
  { id: 'cashin',   label: 'Cashin (entrantes)',  icon: ArrowDownToLine },
  { id: 'cashout',  label: 'Cashout (salientes)', icon: ArrowUpFromLine },
];

const STATUS_COLORS = {
  pending_validation: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  validated:          'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  credited:           'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  invalid:            'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  fraud_suspicion:    'bg-red-100 text-red-900 dark:bg-red-900/40 dark:text-red-200',
  duplicate:          'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
  error:              'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  pending:            'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  processing:         'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  processed:          'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  failed:             'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  rejected:           'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  unknown:            'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
};

const EMPTY_ACCOUNT = {
  name: '',
  env: 'test',
  company: '',
  username: '',
  password: '',
  callback_security_key: '',
  allowed_ips: '',
};

/* ────────── Helpers ────────── */
const fmtMoney = (v) => '$' + Number(v ?? 0).toLocaleString('es-AR', { minimumFractionDigits: 2 });
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('es-AR'); } catch { return iso; }
};

const statusBadge = (status) => (
  <span data-testid={`status-badge-${status}`} className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[status] || 'bg-gray-100 text-gray-800'}`}>
    {status}
  </span>
);


/* ════════════════════════════ COMPONENTE PRINCIPAL ════════════════════════════ */
export default function TotalCoin({ token, currentUser, embedded = false }) {
  const [activeTab, setActiveTab] = useState('cuentas');
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (msg, kind = 'success') => {
    setToast({ msg, kind });
    setTimeout(() => setToast(null), 3500);
  };

  const apiHeaders = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  /* ─── carga cuentas ─── */
  const loadAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/totalcoin/accounts`, { headers: apiHeaders });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setAccounts(data || []);
      if (!selectedAccountId && data?.length) setSelectedAccountId(data[0].id);
    } catch (e) {
      showToast('Error cargando cuentas: ' + e.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [token]);   // eslint-disable-line

  useEffect(() => { loadAccounts(); }, [loadAccounts]);

  return (
    <div data-testid="totalcoin-page" className={embedded ? '' : 'p-6 max-w-7xl mx-auto'}>
      {/* Header (oculto en modo embedded) */}
      {!embedded && (
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Wallet className="w-8 h-8 text-emerald-600" />
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              TotalCoin
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="reload-accounts-btn"
              onClick={loadAccounts}
              disabled={loading}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 text-sm">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Recargar
            </button>
            <button
              data-testid="new-account-btn"
              onClick={() => { setEditingAccount(null); setShowForm(true); }}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium">
              <Plus className="w-4 h-4" />
              Nueva cuenta
            </button>
          </div>
        </div>
      )}

      {/* Barra de acciones para modo embedded */}
      {embedded && (
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            <strong>Pasarela TotalCoin</strong> — gestión de webhook, cashin entrante y cashout saliente.
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="reload-accounts-btn" onClick={loadAccounts} disabled={loading}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 text-sm">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Recargar
            </button>
            <button data-testid="new-account-btn"
              onClick={() => { setEditingAccount(null); setShowForm(true); }}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium">
              <Plus className="w-4 h-4" />
              Nueva cuenta TotalCoin
            </button>
          </div>
        </div>
      )}

      {/* Banner prominente: URL del webhook lista para mandar a TotalCoin */}
      <WebhookUrlBanner accounts={accounts} token={token} showToast={showToast} />

      {/* Toast */}
      {toast && (
        <div data-testid="toast" className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-white ${toast.kind === 'error' ? 'bg-red-600' : 'bg-emerald-600'}`}>
          {toast.msg}
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-800 mb-4">
        <div className="flex gap-1">
          {TABS.map(t => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                data-testid={`tab-${t.id}`}
                onClick={() => setActiveTab(t.id)}
                className={`px-4 py-2.5 flex items-center gap-2 text-sm font-medium border-b-2 transition ${
                  activeTab === t.id
                    ? 'border-emerald-600 text-emerald-700 dark:text-emerald-400'
                    : 'border-transparent text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
                }`}>
                <Icon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Form modal */}
      {showForm && (
        <AccountFormModal
          token={token}
          editing={editingAccount}
          onClose={() => { setShowForm(false); setEditingAccount(null); }}
          onSaved={() => { setShowForm(false); setEditingAccount(null); loadAccounts(); }}
          showToast={showToast}
        />
      )}

      {/* Tab content */}
      {activeTab === 'cuentas' && (
        <AccountsTab
          accounts={accounts}
          token={token}
          loading={loading}
          onEdit={(a) => { setEditingAccount(a); setShowForm(true); }}
          onReload={loadAccounts}
          showToast={showToast}
          selectedAccountId={selectedAccountId}
          setSelectedAccountId={setSelectedAccountId}
        />
      )}
      {activeTab === 'cashin' && (
        <EventsTab
          token={token}
          accounts={accounts}
          selectedAccountId={selectedAccountId}
          setSelectedAccountId={setSelectedAccountId}
          kind="cashin"
          showToast={showToast}
        />
      )}
      {activeTab === 'cashout' && (
        <EventsTab
          token={token}
          accounts={accounts}
          selectedAccountId={selectedAccountId}
          setSelectedAccountId={setSelectedAccountId}
          kind="cashout"
          showToast={showToast}
        />
      )}
    </div>
  );
}

/* ════════════════════════════ BANNER URL DEL WEBHOOK ════════════════════════════ */
/**
 * Banner prominente arriba del tab que muestra la URL del webhook lista para entregar
 * a TotalCoin. Si hay 1+ cuentas, muestra la primera (o la seleccionada). Si no hay
 * cuentas todavía, muestra un CTA grande "Crear cuenta para generar URL".
 */
function WebhookUrlBanner({ accounts, token, showToast }) {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [accountIdx, setAccountIdx] = useState(0);
  const apiHeaders = { 'Authorization': `Bearer ${token}` };

  const account = accounts[accountIdx];

  const loadDetails = useCallback(async () => {
    if (!account) { setDetails(null); return; }
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/totalcoin/accounts/${account.id}/webhook-url`, { headers: apiHeaders });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setDetails(await r.json());
    } catch (e) {
      showToast('Error cargando URL: ' + e.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [account?.id, token]);  // eslint-disable-line

  useEffect(() => { loadDetails(); }, [loadDetails]);

  const copy = (val, label) => navigator.clipboard.writeText(val).then(() => showToast(`${label} copiado`));

  if (!accounts.length) {
    return (
      <div data-testid="webhook-banner-empty" className="mb-6 p-5 rounded-xl border-2 border-dashed border-emerald-300 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-900/10">
        <div className="flex items-center gap-3 mb-2">
          <KeyRound className="w-6 h-6 text-emerald-600" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Webhook URL para entregar a TotalCoin</h3>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Todavía no hay ninguna cuenta TotalCoin configurada. Click en <strong>Nueva cuenta TotalCoin</strong> arriba para crearla y generar la URL automáticamente.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="webhook-banner" className="mb-6 p-5 rounded-xl border-2 border-emerald-300 dark:border-emerald-800 bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/10">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-emerald-600 text-white">
            <KeyRound className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">URL del webhook para TotalCoin</h3>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              Copiá y pegá estas URLs en el portal de TotalCoin para que empiecen a enviarnos los pagos.
            </p>
          </div>
        </div>
        {accounts.length > 1 && (
          <select data-testid="banner-account-selector" value={accountIdx}
            onChange={e => setAccountIdx(parseInt(e.target.value, 10))}
            className="px-3 py-1.5 text-sm rounded border border-emerald-300 bg-white dark:bg-gray-800">
            {accounts.map((a, i) => <option key={a.id} value={i}>{a.name} ({a.env})</option>)}
          </select>
        )}
      </div>

      {loading && <div className="text-sm text-gray-500"><Loader2 className="w-4 h-4 animate-spin inline mr-1" />Cargando…</div>}

      {details && (
        <div className="space-y-2.5 mt-3">
          <BannerUrlRow
            label="🟢 Cashin (pagos entrantes)"
            value={details.cashin_webhook_url}
            onCopy={copy}
            testid="banner-copy-cashin"
            description="Esta URL recibe el aviso cuando un cliente paga (transferencia, QR, tarjeta)."
          />
          <BannerUrlRow
            label="🔵 Cashout (callback de pagos salientes)"
            value={details.cashout_callback_url}
            onCopy={copy}
            testid="banner-copy-cashout"
            description="Esta URL recibe el resultado firmado SHA256 cuando un pago saliente se procesa."
          />
          {details.basic_auth && (
            <details className="mt-2">
              <summary className="cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900">
                🔐 Basic Auth (opcional, si TotalCoin permite headers personalizados)
              </summary>
              <div className="mt-2 ml-4 space-y-1.5">
                <BannerUrlRow label="user" value={details.basic_auth.user} onCopy={copy} testid="banner-copy-bauser" small />
                <BannerUrlRow label="password" value={details.basic_auth.password} onCopy={copy} testid="banner-copy-bapass" small />
              </div>
            </details>
          )}
          <div className="text-xs text-gray-600 dark:text-gray-400 mt-3 pt-3 border-t border-emerald-200 dark:border-emerald-800">
            💡 <strong>Lo que pedirle a TotalCoin:</strong> credenciales de API (username, password, company),
            la <strong>SecurityKey</strong> privada para validar la firma SHA256,
            y la lista de <strong>IPs</strong> desde donde envían los webhooks (para nuestro firewall).
          </div>
        </div>
      )}
    </div>
  );
}

function BannerUrlRow({ label, value, onCopy, testid, description, small }) {
  return (
    <div>
      <div className={`flex items-center justify-between gap-2 ${small ? '' : 'mb-1'}`}>
        <span className={`font-semibold ${small ? 'text-xs text-gray-600 w-20' : 'text-sm text-gray-900 dark:text-gray-100'}`}>{label}</span>
        <button data-testid={testid} onClick={() => onCopy(value, label)}
          className={`flex items-center gap-1.5 px-3 py-1 rounded text-xs font-medium ${small ? 'bg-gray-100 hover:bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200' : 'bg-emerald-600 hover:bg-emerald-700 text-white'}`}>
          <Copy className="w-3 h-3" /> Copiar
        </button>
      </div>
      <code className={`block w-full font-mono ${small ? 'text-xs' : 'text-xs'} bg-white dark:bg-gray-950 px-3 py-2 rounded border border-emerald-200 dark:border-emerald-900/50 break-all`} title={value}>
        {value}
      </code>
      {description && <p className="text-xs text-gray-500 mt-1">{description}</p>}
    </div>
  );
}


/* ════════════════════════════ TAB CUENTAS ════════════════════════════ */
function AccountsTab({ accounts, token, loading, onEdit, onReload, showToast, selectedAccountId, setSelectedAccountId }) {
  if (loading && !accounts.length) {
    return <div className="text-center py-10 text-gray-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  }
  if (!accounts.length) {
    return (
      <Card>
        <CardContent className="p-10 text-center">
          <Wallet className="w-14 h-14 mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500 mb-4">No hay cuentas TotalCoin configuradas.</p>
          <p className="text-sm text-gray-400">Click en <strong>Nueva cuenta</strong> arriba para crear la primera.</p>
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {accounts.map(acc => (
        <AccountCard
          key={acc.id}
          account={acc}
          token={token}
          onEdit={() => onEdit(acc)}
          onReload={onReload}
          showToast={showToast}
          isSelected={selectedAccountId === acc.id}
          onSelect={() => setSelectedAccountId(acc.id)}
        />
      ))}
    </div>
  );
}

function AccountCard({ account, token, onEdit, onReload, showToast, isSelected, onSelect }) {
  const [webhookUrl, setWebhookUrl] = useState(null);
  const [showSecrets, setShowSecrets] = useState(false);
  const [testing, setTesting] = useState(false);
  const apiHeaders = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const loadWebhookUrl = async () => {
    try {
      const r = await fetch(`${API_URL}/api/totalcoin/accounts/${account.id}/webhook-url`, { headers: apiHeaders });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setWebhookUrl(data);
      setShowSecrets(true);
    } catch (e) {
      showToast('Error cargando webhook URL: ' + e.message, 'error');
    }
  };

  const copy = (val, label) => {
    navigator.clipboard.writeText(val).then(() => showToast(`${label} copiado`));
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const r = await fetch(`${API_URL}/api/totalcoin/accounts/${account.id}/test`, {
        method: 'POST', headers: apiHeaders,
      });
      const data = await r.json();
      if (data.ok) showToast(`Conexión OK (${data.env})`);
      else showToast(`Falló: ${data.message || data.status_code}`, 'error');
    } catch (e) {
      showToast('Error: ' + e.message, 'error');
    } finally {
      setTesting(false);
    }
  };

  const regenerateSecret = async () => {
    if (!confirm('¿Regenerar el secret? Tenés que entregar las nuevas URLs a TotalCoin.')) return;
    try {
      const r = await fetch(`${API_URL}/api/totalcoin/accounts/${account.id}/regenerate-secret`, {
        method: 'POST', headers: apiHeaders,
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setWebhookUrl({ ...webhookUrl, ...data });
      showToast('Secret regenerado, copiá las nuevas URLs');
    } catch (e) {
      showToast('Error: ' + e.message, 'error');
    }
  };

  const handleDelete = async () => {
    if (!confirm(`¿Eliminar la cuenta "${account.name}"? Se hace soft-delete.`)) return;
    try {
      const r = await fetch(`${API_URL}/api/totalcoin/accounts/${account.id}`, { method: 'DELETE', headers: apiHeaders });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      showToast('Cuenta eliminada');
      onReload();
    } catch (e) {
      showToast('Error: ' + e.message, 'error');
    }
  };

  return (
    <Card data-testid={`account-card-${account.id}`} className={`${isSelected ? 'ring-2 ring-emerald-500' : ''}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className={`p-2 rounded-lg ${account.env === 'prod' ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-yellow-100 dark:bg-yellow-900/30'}`}>
              <Wallet className={`w-5 h-5 ${account.env === 'prod' ? 'text-emerald-700 dark:text-emerald-300' : 'text-yellow-700 dark:text-yellow-300'}`} />
            </div>
            <div>
              <CardTitle className="text-base">{account.name}</CardTitle>
              <div className="flex gap-2 mt-1">
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${account.env === 'prod' ? 'bg-emerald-200 text-emerald-900 dark:bg-emerald-900 dark:text-emerald-100' : 'bg-yellow-200 text-yellow-900 dark:bg-yellow-900 dark:text-yellow-100'}`}>
                  {account.env?.toUpperCase()}
                </span>
                <span className="text-xs text-gray-500">company: {account.company}</span>
              </div>
            </div>
          </div>
          <div className="flex gap-1">
            <button data-testid={`select-account-btn-${account.id}`} onClick={onSelect} title="Seleccionar para ver eventos" className={`p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 ${isSelected ? 'text-emerald-600' : 'text-gray-400'}`}>
              <CheckCircle2 className="w-4 h-4" />
            </button>
            <button data-testid={`edit-account-btn-${account.id}`} onClick={onEdit} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500"><Pencil className="w-4 h-4" /></button>
            <button data-testid={`delete-account-btn-${account.id}`} onClick={handleDelete} className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500"><Trash2 className="w-4 h-4" /></button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0 space-y-3">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          <div>Username: <code className="text-xs bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded">{account.username}</code></div>
          <div className="mt-1">IPs whitelisted: {(account.allowed_ips || []).length === 0 ? <span className="text-yellow-600">ninguna (modo descubrimiento)</span> : <code>{account.allowed_ips.join(', ')}</code>}</div>
        </div>

        <div className="flex flex-wrap gap-2">
          <button data-testid={`test-conn-btn-${account.id}`} onClick={testConnection} disabled={testing}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 disabled:opacity-50">
            {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}
            Test conexión
          </button>
          <button data-testid={`show-webhook-btn-${account.id}`} onClick={() => webhookUrl ? setShowSecrets(s => !s) : loadWebhookUrl()}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs bg-emerald-50 hover:bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
            {showSecrets ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            {showSecrets && webhookUrl ? 'Ocultar URL' : 'Ver URL del webhook'}
          </button>
          <button data-testid={`regen-secret-btn-${account.id}`} onClick={regenerateSecret}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs bg-orange-50 hover:bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300">
            <RotateCw className="w-3.5 h-3.5" />
            Regenerar secret
          </button>
        </div>

        {showSecrets && webhookUrl && (
          <div className="mt-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-800 space-y-2">
            <UrlRow label="Cashin webhook" value={webhookUrl.cashin_webhook_url} onCopy={copy} testid={`copy-cashin-url-${account.id}`} />
            <UrlRow label="Cashout callback" value={webhookUrl.cashout_callback_url} onCopy={copy} testid={`copy-cashout-url-${account.id}`} />
            {webhookUrl.basic_auth && (
              <div className="text-xs">
                <div className="text-gray-500 mb-1">Basic Auth (opcional):</div>
                <UrlRow label="user" value={webhookUrl.basic_auth.user} onCopy={copy} small testid={`copy-ba-user-${account.id}`} />
                <UrlRow label="password" value={webhookUrl.basic_auth.password} onCopy={copy} small testid={`copy-ba-pass-${account.id}`} />
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function UrlRow({ label, value, onCopy, small, testid }) {
  return (
    <div className="flex items-center gap-2">
      <span className={`text-gray-500 ${small ? 'text-xs w-16' : 'text-xs w-32'}`}>{label}:</span>
      <code className={`flex-1 truncate font-mono ${small ? 'text-xs' : 'text-xs'} bg-white dark:bg-gray-950 px-2 py-1 rounded border border-gray-200 dark:border-gray-800`} title={value}>
        {value}
      </code>
      <button data-testid={testid} onClick={() => onCopy(value, label)} className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded">
        <Copy className="w-3.5 h-3.5 text-gray-500" />
      </button>
    </div>
  );
}

/* ════════════════════════════ FORM MODAL ════════════════════════════ */
function AccountFormModal({ token, editing, onClose, onSaved, showToast }) {
  const [form, setForm] = useState(editing ? {
    name: editing.name || '',
    env: editing.env || 'test',
    company: editing.company || '',
    username: editing.username || '',
    password: '',
    callback_security_key: '',
    allowed_ips: (editing.allowed_ips || []).join(', '),
  } : EMPTY_ACCOUNT);
  const [saving, setSaving] = useState(false);
  const [resultUrls, setResultUrls] = useState(null);

  const apiHeaders = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name || !form.company || !form.username) {
      showToast('Faltan campos: name, company, username', 'error'); return;
    }
    if (!editing && (!form.password || !form.callback_security_key)) {
      showToast('Falta password y security key', 'error'); return;
    }
    setSaving(true);
    try {
      const body = {
        ...form,
        allowed_ips: form.allowed_ips
          ? form.allowed_ips.split(',').map(s => s.trim()).filter(Boolean)
          : [],
      };
      const url = editing
        ? `${API_URL}/api/totalcoin/accounts/${editing.id}`
        : `${API_URL}/api/totalcoin/accounts`;
      const method = editing ? 'PUT' : 'POST';
      const r = await fetch(url, { method, headers: apiHeaders, body: JSON.stringify(body) });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      const data = await r.json();
      if (!editing) {
        setResultUrls(data);  // Mostrar URLs generadas
        showToast('Cuenta creada — copiá las URLs');
      } else {
        showToast('Cuenta actualizada');
        onSaved();
      }
    } catch (e) {
      showToast('Error: ' + e.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const copy = (v) => navigator.clipboard.writeText(v).then(() => showToast('Copiado'));

  return (
    <div className="fixed inset-0 bg-black/50 z-40 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-900 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 data-testid="form-title" className="text-xl font-bold">{editing ? 'Editar cuenta' : 'Nueva cuenta TotalCoin'}</h2>
          <button data-testid="close-form-btn" onClick={onClose} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"><X className="w-5 h-5" /></button>
        </div>

        {!resultUrls && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Nombre" testid="form-name">
                <input data-testid="form-name-input" value={form.name} onChange={e => update('name', e.target.value)}
                  className="w-full px-3 py-2 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm" placeholder="ej: TotalCoin Producción" />
              </Field>
              <Field label="Ambiente" testid="form-env">
                <select data-testid="form-env-select" value={form.env} onChange={e => update('env', e.target.value)}
                  className="w-full px-3 py-2 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm">
                  <option value="test">TEST (apicobranzastest)</option>
                  <option value="prod">PROD (apicobranzas)</option>
                </select>
              </Field>
              <Field label="Company" testid="form-company">
                <input data-testid="form-company-input" value={form.company} onChange={e => update('company', e.target.value)}
                  className="w-full px-3 py-2 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm" placeholder="empresa que da TotalCoin" />
              </Field>
              <Field label="Username (API)" testid="form-username">
                <input data-testid="form-username-input" value={form.username} onChange={e => update('username', e.target.value)}
                  className="w-full px-3 py-2 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm" placeholder="username de la API" />
              </Field>
              <Field label={editing ? 'Password (vacío = no cambiar)' : 'Password (API)'} testid="form-password">
                <input data-testid="form-password-input" type="password" value={form.password} onChange={e => update('password', e.target.value)}
                  className="w-full px-3 py-2 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm" />
              </Field>
              <Field label={editing ? 'Security Key (vacío = no cambiar)' : 'Security Key (callback SHA256)'} testid="form-secret">
                <input data-testid="form-securitykey-input" type="password" value={form.callback_security_key} onChange={e => update('callback_security_key', e.target.value)}
                  className="w-full px-3 py-2 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm" />
              </Field>
            </div>
            <div className="mt-3">
              <Field label="IPs whitelist (separadas por coma, opcional)" testid="form-ips">
                <input data-testid="form-allowed-ips" value={form.allowed_ips} onChange={e => update('allowed_ips', e.target.value)}
                  className="w-full px-3 py-2 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm" placeholder="ej: 200.51.x.y, 200.51.x.z" />
              </Field>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button data-testid="cancel-form-btn" onClick={onClose} className="px-4 py-2 rounded border border-gray-300 dark:border-gray-700 text-sm">Cancelar</button>
              <button data-testid="save-form-btn" onClick={submit} disabled={saving}
                className="px-4 py-2 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-sm disabled:opacity-50 flex items-center gap-2">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {editing ? 'Guardar' : 'Crear cuenta'}
              </button>
            </div>
          </>
        )}

        {/* Confirmación con URLs generadas */}
        {resultUrls && (
          <div className="space-y-4">
            <div className="p-3 rounded bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300 flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-medium">Cuenta creada correctamente.</div>
                <div className="text-sm mt-1">Copiá estas URLs y entregalas al equipo de TotalCoin para configurar el webhook.</div>
              </div>
            </div>
            <div className="space-y-2">
              <UrlRow label="Cashin webhook" value={resultUrls.cashin_webhook_url} onCopy={copy} testid="result-copy-cashin" />
              <UrlRow label="Cashout callback" value={resultUrls.cashout_callback_url} onCopy={copy} testid="result-copy-cashout" />
              {resultUrls.basic_auth && (
                <>
                  <div className="text-xs text-gray-500 mt-2">Basic Auth (opcional):</div>
                  <UrlRow label="user" value={resultUrls.basic_auth.user} onCopy={copy} small testid="result-copy-bauser" />
                  <UrlRow label="password" value={resultUrls.basic_auth.password} onCopy={copy} small testid="result-copy-bapass" />
                </>
              )}
            </div>
            <div className="flex justify-end">
              <button data-testid="finish-form-btn" onClick={onSaved} className="px-4 py-2 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-sm">Listo</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children, testid }) {
  return (
    <label data-testid={`field-${testid}`} className="block">
      <span className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</span>
      {children}
    </label>
  );
}


/* ════════════════════════════ TAB EVENTOS (cashin/cashout) ════════════════════════════ */
function EventsTab({ token, accounts, selectedAccountId, setSelectedAccountId, kind, showToast }) {
  const [events, setEvents] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const apiHeaders = { 'Authorization': `Bearer ${token}` };
  const endpoint = kind === 'cashin' ? 'cashin-events' : 'cashout-requests';

  const load = useCallback(async () => {
    if (!selectedAccountId) { setEvents([]); return; }
    setLoading(true);
    try {
      const qs = statusFilter ? `?status=${statusFilter}&limit=100` : '?limit=100';
      const r = await fetch(`${API_URL}/api/totalcoin/accounts/${selectedAccountId}/${endpoint}${qs}`, { headers: apiHeaders });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setEvents(await r.json());
    } catch (e) {
      showToast('Error: ' + e.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [selectedAccountId, statusFilter, endpoint, token]);  // eslint-disable-line

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!autoRefresh) return;
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [autoRefresh, load]);

  const revalidate = async (eventId) => {
    try {
      const r = await fetch(`${API_URL}/api/totalcoin/cashin-events/${eventId}/revalidate`, { method: 'POST', headers: apiHeaders });
      const data = await r.json();
      showToast(data.ok ? 'Revalidado OK' : `Falló: ${data.message}`, data.ok ? 'success' : 'error');
      load();
    } catch (e) {
      showToast('Error: ' + e.message, 'error');
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select data-testid="account-selector" value={selectedAccountId || ''} onChange={e => setSelectedAccountId(e.target.value)}
          className="px-3 py-2 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm">
          <option value="">— Seleccionar cuenta —</option>
          {accounts.map(a => <option key={a.id} value={a.id}>{a.name} ({a.env})</option>)}
        </select>
        <select data-testid="status-filter" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="px-3 py-2 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm">
          <option value="">Todos los estados</option>
          {kind === 'cashin'
            ? ['pending_validation', 'validated', 'credited', 'invalid', 'fraud_suspicion', 'duplicate', 'error'].map(s => <option key={s} value={s}>{s}</option>)
            : ['pending', 'processing', 'processed', 'failed', 'rejected', 'unknown'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <label className="flex items-center gap-1 text-sm">
          <input data-testid="autorefresh-toggle" type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} />
          Auto-refresh 10s
        </label>
        <button data-testid="reload-events-btn" onClick={load} className="ml-auto px-3 py-2 rounded border border-gray-300 dark:border-gray-700 text-sm flex items-center gap-2">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Recargar
        </button>
      </div>

      {!selectedAccountId ? (
        <Card><CardContent className="p-10 text-center text-gray-500">Seleccioná una cuenta para ver los eventos.</CardContent></Card>
      ) : !events.length ? (
        <Card><CardContent className="p-10 text-center text-gray-500">No hay {kind} events todavía.</CardContent></Card>
      ) : kind === 'cashin' ? (
        <CashinTable events={events} onRevalidate={revalidate} />
      ) : (
        <CashoutTable events={events} />
      )}
    </div>
  );
}

function CashinTable({ events, onRevalidate }) {
  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 overflow-x-auto">
      <table data-testid="cashin-table" className="w-full text-sm">
        <thead className="bg-gray-50 dark:bg-gray-800/50 text-xs">
          <tr>
            <Th>Fecha</Th><Th>Referencia</Th><Th>Monto</Th><Th>Método</Th><Th>Emisor</Th><Th>Estado</Th><Th>Acciones</Th>
          </tr>
        </thead>
        <tbody>
          {events.map(ev => (
            <tr key={ev.id} data-testid={`cashin-row-${ev.id}`} className="border-t border-gray-100 dark:border-gray-800">
              <Td className="text-xs whitespace-nowrap">{fmtDate(ev.received_at)}</Td>
              <Td><code className="text-xs">{ev.referencia}</code></Td>
              <Td className="font-mono">{fmtMoney(ev.monto)}</Td>
              <Td className="text-xs">{ev.metodo_pago || '—'}</Td>
              <Td className="text-xs"><div>{ev.nombre_emisor || '—'}</div><div className="text-gray-400">{ev.cuit_emisor}</div></Td>
              <Td>{statusBadge(ev.status)}</Td>
              <Td>
                {ev.status === 'pending_validation' && (
                  <button data-testid={`revalidate-btn-${ev.id}`} onClick={() => onRevalidate(ev.id)}
                    className="text-xs text-blue-600 hover:underline">Revalidar</button>
                )}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CashoutTable({ events }) {
  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 overflow-x-auto">
      <table data-testid="cashout-table" className="w-full text-sm">
        <thead className="bg-gray-50 dark:bg-gray-800/50 text-xs">
          <tr>
            <Th>Fecha</Th><Th>Reference</Th><Th>Monto</Th><Th>Destino</Th><Th>Estado</Th><Th>Firma</Th>
          </tr>
        </thead>
        <tbody>
          {events.map(ev => (
            <tr key={ev.id} data-testid={`cashout-row-${ev.id}`} className="border-t border-gray-100 dark:border-gray-800">
              <Td className="text-xs whitespace-nowrap">{fmtDate(ev.created_at)}</Td>
              <Td><code className="text-xs">{ev.reference}</code></Td>
              <Td className="font-mono">{fmtMoney(ev.monto)}</Td>
              <Td className="text-xs">
                <div>{ev.cuit_destino}</div>
                {ev.alias_destino && <div className="text-gray-400">alias: {ev.alias_destino}</div>}
                {ev.cbu_cvu_destino && <div className="text-gray-400">cbu: {ev.cbu_cvu_destino}</div>}
              </Td>
              <Td>{statusBadge(ev.status)}</Td>
              <Td>
                {ev.callback_received
                  ? (ev.callback_signature_valid
                      ? <span className="text-green-600 text-xs flex items-center gap-1"><Check className="w-3 h-3" />OK</span>
                      : <span className="text-red-600 text-xs flex items-center gap-1"><AlertTriangle className="w-3 h-3" />MAL</span>)
                  : <span className="text-gray-400 text-xs">esperando…</span>}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const Th = ({ children }) => <th className="text-left px-3 py-2 font-semibold text-gray-700 dark:text-gray-300">{children}</th>;
const Td = ({ children, className = '' }) => <td className={`px-3 py-2 ${className}`}>{children}</td>;
